import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { useToFill } from './shared/useToFill'

const PHENO_CODES = ['DE', 'JI', 'KR', 'LE', 'ML', 'NA', 'RO', 'SN', 'SV']

export function usePhenomenaDefinitions() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['phenomenaDefinitions'],
    queryFn: () => api.get(`/phenomena?pheno=[${PHENO_CODES.join(',')}]&svg=1&cache=[-1]`),
    staleTime: 60 * 60 * 1000,
  })

  // Map: code → { code, name, svg }
  // API vrací { value: [...], Count: N }, každý item má: abbreviation, name, svg_content
  const defsMap = new Map()
  const items = Array.isArray(data) ? data : (data?.value ?? [])
  items.forEach(item => {
    const code = item.abbreviation
    if (code) defsMap.set(code, { code, name: item.name, svg: item.svg_content })
  })

  return { defsMap, isLoading, isError }
}

export function usePhenomenaDayData(ghId, year, month) {
  const mm = String(month).padStart(2, '0')
  const from = `${year}-${mm}`

  const queryKey = ['phenomenaDayData', ghId, year, month]

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => api.get(`/phenomena_day?gh_id=${ghId}&from=${from}&to=${from}`)
      .then(r => ({ result: r.data ?? [], toFill: r.to_fill ?? [] })),
    enabled: !!ghId,
    staleTime: 5 * 60 * 1000,
  })

  // Živé = zobrazený měsíc je aktuální (jevy dnešního dne ještě přibývají; server
  // dotahuje jen k včerejšku, viz doc/plans/phenomena-no-today.md).
  const now = new Date()
  const live = Number(year) === now.getUTCFullYear() && Number(month) === now.getUTCMonth() + 1
  useToFill(queryKey, data, (_result, resp) => resp.data, { gran: 'DLY', live })

  // Map: "YYYY-MM-DD" → Set of phenomenon codes present that day
  // API vrací { data: [...], to_fill: [...] }, každý item má: phenomenon, dt (string "YYYY-MM-DD")
  const dayMap = new Map()
  const rows = Array.isArray(data?.result) ? data.result : []
  rows.forEach(row => {
    const key = row.dt ?? row.date
    if (!key || !row.phenomenon) return
    const codes = dayMap.get(key) ?? new Set()
    codes.add(row.phenomenon)
    dayMap.set(key, codes)
  })

  return { dayMap, isLoading, isError }
}

export { PHENO_CODES }
